import { readLocalJson, readSession, writeLocalJson, writeSession } from '../core/storage'
import { nowStamp, today } from '../core/format/dates'
import { DEMO_ADMIN, DEMO_COLLEAGUE, DEMO_MARKETING } from './seed/admin'
import type { StaffLogin } from '../core/types/adminRequests'
import type { AdminSessionInfo, AuditEntry, RoleId } from '../core/types/team'

/**
 * DEMO — de accounts (logins) bij de personen, hun rollen en sessies, en het logboek
 * (docs/RECHTEN-EN-SCHAAL.md §3, §10.5, §10.7). In localStorage, zodat een tweede tabblad
 * hetzelfde team ziet: trekt Sarah in het ene tabblad de login van Mila in, dan is Mila in het
 * andere bij haar volgende klik uitgelogd. De server bewaart dit in `admin_accounts`,
 * `admin_sessions` en `audit_entries`.
 */

export type DemoAccount = {
  accountId: string
  staffId: string
  email: string
  status: 'uitgenodigd' | 'actief'
  lastLoginAt?: string
  roles: RoleId[]
  validUntil: string | null
  /** Verhoogd bij "uitloggen op alle apparaten" en bij stoppen: oudere sessies vervallen. */
  epoch: number
  sessions: AdminSessionInfo[]
}

// v2: met de marketeer erbij.
const KEY = 'bb-demo-team-v2'
const AUDIT_KEY = 'bb-demo-audit-v1'
const TAB_KEY = 'bb-demo-admin-tab'
const EPOCH_KEY = 'bb-demo-admin-epoch'
const REAUTH_KEY = 'bb-demo-admin-reauth'
/** Een bevestiging geldt 15 minuten, net als op de server. */
const REAUTH_MS = 15 * 60 * 1000
const AUDIT_MAX = 1000

const SEED: DemoAccount[] = [
  { accountId: 'acc-sarah', staffId: 'sarah', email: DEMO_ADMIN.email, status: 'actief', roles: ['eigenaar'], validUntil: null, epoch: 0, sessions: [] },
  { accountId: 'acc-mila', staffId: DEMO_COLLEAGUE.staffId, email: DEMO_COLLEAGUE.email, status: 'actief', roles: ['behandelaar-zzp'], validUntil: null, epoch: 0, sessions: [] },
  { accountId: 'acc-noor', staffId: DEMO_MARKETING.staffId, email: DEMO_MARKETING.email, status: 'actief', roles: ['marketing'], validUntil: null, epoch: 0, sessions: [] },
]

const load = (): DemoAccount[] => readLocalJson<DemoAccount[]>(KEY, SEED)
const save = (list: DemoAccount[]) => writeLocalJson(KEY, list)

/** Een vaste id voor dit tabblad: in de demo is één tabblad één sessie. */
function tabId(): string {
  const known = readSession(TAB_KEY)
  if (known) return known
  const id = `tab-${Math.random().toString(36).slice(2, 10)}`
  writeSession(TAB_KEY, id)
  return id
}

function deviceLabel(): string {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent
  const device = /iPhone/.test(ua) ? 'iPhone' : /iPad/.test(ua) ? 'iPad' : /Android/.test(ua) ? 'Android' : /Mac/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : 'Computer'
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : /Firefox\//.test(ua) ? 'Firefox' : 'Browser'
  return `${device} · ${browser}`
}

export const demoTeam = {
  list: load,
  byStaff: (staffId: string): DemoAccount | null => load().find((a) => a.staffId === staffId) ?? null,
  byEmail: (email: string): DemoAccount | null => load().find((a) => a.email === email) ?? null,

  /** Bestaat de login, en is hij niet verlopen? */
  usable(a: DemoAccount | null): a is DemoAccount {
    return a !== null && (!a.validUntil || a.validUntil >= today())
  },

  put(a: DemoAccount) {
    save([...load().filter((x) => x.staffId !== a.staffId), a])
  },
  remove(staffId: string) {
    save(load().filter((x) => x.staffId !== staffId))
  },
  change(staffId: string, fn: (a: DemoAccount) => DemoAccount): DemoAccount | null {
    const current = load().find((x) => x.staffId === staffId)
    if (!current) return null
    const next = fn(current)
    save(load().map((x) => (x.staffId === staffId ? next : x)))
    return next
  },

  /** Ingelogd in dit tabblad: sessie erbij, "laatst ingelogd" bijwerken. */
  signedIn(staffId: string) {
    const id = tabId()
    const at = nowStamp()
    const next = demoTeam.change(staffId, (a) => ({
      ...a,
      status: 'actief',
      lastLoginAt: at,
      sessions: [{ id, device: deviceLabel(), lastActiveAt: at }, ...a.sessions.filter((s) => s.id !== id)].slice(0, 5),
    }))
    if (next) writeSession(EPOCH_KEY, String(next.epoch))
    // Net ingelogd telt als bevestigd (zoals Laravel `password.confirm` na een login).
    demoTeam.reauthed()
  },
  signedOut(staffId: string) {
    const id = tabId()
    demoTeam.change(staffId, (a) => ({ ...a, sessions: a.sessions.filter((s) => s.id !== id) }))
    writeSession(REAUTH_KEY, '')
  },
  /** Is de sessie in dit tabblad nog geldig (niet intussen overal uitgelogd)? */
  sessionValid(a: DemoAccount): boolean {
    return readSession(EPOCH_KEY) === String(a.epoch) && a.sessions.some((s) => s.id === tabId())
  },
  revokeSessions(staffId: string): DemoAccount | null {
    return demoTeam.change(staffId, (a) => ({ ...a, epoch: a.epoch + 1, sessions: [] }))
  },

  reauthed() {
    writeSession(REAUTH_KEY, String(Date.now()))
  },
  reauthFresh(): boolean {
    const at = Number(readSession(REAUTH_KEY) ?? 0)
    return at > 0 && Date.now() - at < REAUTH_MS
  },

  /** Zoals GET /admin/staff/logins het geeft. */
  toLogin(a: DemoAccount, viewerStaffId: string): StaffLogin {
    const id = tabId()
    return {
      staffId: a.staffId,
      email: a.email,
      status: a.status,
      ...(a.lastLoginAt ? { lastLoginAt: a.lastLoginAt } : {}),
      accountId: a.accountId,
      roles: a.roles,
      validUntil: a.validUntil,
      sessions: a.sessions.map((s) => ({ ...s, ...(s.id === id && a.staffId === viewerStaffId ? { current: true } : {}) })),
    }
  },
}

/** Het logboek: alleen toevoegen, nieuwste eerst. */
export const demoAudit = {
  list: (): AuditEntry[] => readLocalJson<AuditEntry[]>(AUDIT_KEY, []),
  add(e: Omit<AuditEntry, 'id' | 'at'>) {
    const entry: AuditEntry = { ...e, id: `log-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`, at: nowStamp(), device: deviceLabel() }
    writeLocalJson(AUDIT_KEY, [entry, ...demoAudit.list()].slice(0, AUDIT_MAX))
  },
  /** Wie dit ding het laatst opende (vóór nu), voor "Laatst ingezien door …". */
  lastViewed(targetId: string): { name: string; at: string } | null {
    const hit = demoAudit.list().find((e) => e.target.id === targetId && e.cls === 'C' && e.target.kind === 'klant')
    return hit ? { name: hit.name, at: hit.at } : null
  },
}
